import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Tab, Tabs, Table, Spinner, Button, Alert, Form, Modal } from "react-bootstrap";
import {
  MdDelete,
  MdDownload,
  MdUpload,
  MdWarning,
  MdDeleteForever
} from "react-icons/md";
import eliminarReservasApi from "../../../services/api/eliminarReservasApi";
import * as XLSX from "xlsx";
import "react-toastify/dist/ReactToastify.css";
import "../../../components/css/Dashboard.css";

const EliminarReservas = () => {
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject"));

  // Estados generales
  const [activeTab, setActiveTab] = useState("individual");

  // Estados para eliminación individual
  const [dniIndividual, setDniIndividual] = useState("");
  const [reservaEncontrada, setReservaEncontrada] = useState(null);
  const [buscandoReserva, setBuscandoReserva] = useState(false);
  const [eliminandoIndividual, setEliminandoIndividual] = useState(false);

  // Estados para eliminación masiva
  const [fileMasiva, setFileMasiva] = useState(null);
  const [procesandoMasiva, setProcesandoMasiva] = useState(false);
  const [resultadosMasiva, setResultadosMasiva] = useState(null);

  // Estados para eliminar todas
  const [showModalEliminarTodas, setShowModalEliminarTodas] = useState(false);
  const [confirmacionTexto, setConfirmacionTexto] = useState("");
  const [eliminandoTodas, setEliminandoTodas] = useState(false);

  // Estado para descarga
  const [descargando, setDescargando] = useState(false);

  const showToast = (type, message) => {
    if (type === "success") {
      toast.success(message, { position: "bottom-right", autoClose: 3000 });
    } else if (type === "error") {
      toast.error(message, { position: "bottom-right", autoClose: 3000 });
    } else if (type === "info") {
      toast.info(message, { position: "bottom-right", autoClose: 3000 });
    } else if (type === "warning") {
      toast.warning(message, { position: "bottom-right", autoClose: 3000 });
    }
  };

  // ========== DESCARGAR RESERVAS ACTUALES ==========
  const handleDescargarReservasActuales = async () => {
    setDescargando(true);
    try {
      const response = await eliminarReservasApi.descargarReservasActuales();
      
      // Crear blob y descargar
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `reservas_actuales_proyecto_${selectedProject?.id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showToast("success", "Archivo descargado exitosamente");
    } catch (error) {
      console.error("Error al descargar reservas:", error);
      showToast("error", error.response?.data?.detail || "Error al descargar el archivo");
    } finally {
      setDescargando(false);
    }
  };

  // ========== BÚSQUEDA DE RESERVA ==========
  const handleBuscarReserva = async () => {
    if (!dniIndividual || dniIndividual.trim() === "") {
      showToast("error", "Por favor ingrese un DNI");
      return;
    }

    setBuscandoReserva(true);
    setReservaEncontrada(null);

    try {
      const response = await eliminarReservasApi.buscarReservaPorDni(dniIndividual.trim());
      
      if (response.data?.success) {
        setReservaEncontrada(response.data.data);
        showToast("success", "Reserva encontrada");
      } else {
        showToast("error", response.data?.message || "Reserva no encontrada");
        setReservaEncontrada(null);
      }
    } catch (error) {
      console.error("Error al buscar reserva:", error);
      showToast("error", error.response?.data?.detail || "Error al buscar la reserva");
      setReservaEncontrada(null);
    } finally {
      setBuscandoReserva(false);
    }
  };

  // ========== ELIMINACIÓN INDIVIDUAL ==========
  const handleEliminarIndividual = async () => {
    if (!reservaEncontrada) {
      showToast("error", "Primero debe buscar una reserva");
      return;
    }

    setEliminandoIndividual(true);
    try {
      const response = await eliminarReservasApi.eliminarReservaIndividual(dniIndividual.trim());
      
      if (response.data?.success) {
        const data = response.data.data || {};
        showToast("success", `✅ Reserva eliminada: ${data.trabajador} - Cama ${data.cama}`);
        setDniIndividual("");
        setReservaEncontrada(null);
      } else {
        showToast("error", response.data?.message || "Error al eliminar la reserva");
      }
    } catch (error) {
      console.error("Error al eliminar reserva individual:", error);
      showToast("error", error.response?.data?.detail || "Error al eliminar la reserva");
    } finally {
      setEliminandoIndividual(false);
    }
  };

  // ========== ELIMINACIÓN MASIVA ==========
  const handleFileMasivaChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFileMasiva(selectedFile);
      setResultadosMasiva(null);
    }
  };

  const downloadTemplateMasiva = () => {
    const template = [
      { DNI: "12345678" },
      { DNI: "87654321" }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DNIs");
    XLSX.writeFile(wb, "plantilla_eliminar_reservas.xlsx");
    showToast("info", "Plantilla descargada");
  };

  const handleEliminarMasiva = async () => {
    if (!fileMasiva) {
      showToast("error", "Por favor seleccione un archivo");
      return;
    }

    setProcesandoMasiva(true);
    setResultadosMasiva(null);

    try {
      const response = await eliminarReservasApi.eliminarReservasMasiva(fileMasiva);

      if (response.data?.success) {
        setResultadosMasiva(response.data);
        const { eliminadas, fallidas } = response.data;
        
        if (fallidas === 0) {
          showToast("success", `✅ Todas las reservas fueron eliminadas (${eliminadas})`);
        } else {
          showToast(
            "warning",
            `Eliminación completada: ${eliminadas} exitosas, ${fallidas} fallidas`
          );
        }
      } else {
        showToast("error", response.data?.message || "Error al procesar eliminación masiva");
      }
    } catch (error) {
      console.error("Error al eliminar masiva:", error);
      showToast("error", error.response?.data?.detail || "Error al procesar eliminación masiva");
    } finally {
      setProcesandoMasiva(false);
    }
  };

  // ========== ELIMINAR TODAS ==========
  const handleShowModalEliminarTodas = () => {
    setConfirmacionTexto("");
    setShowModalEliminarTodas(true);
  };

  const handleEliminarTodas = async () => {
    if (confirmacionTexto !== "CONFIRMAR") {
      showToast("error", "Debe escribir 'CONFIRMAR' para continuar");
      return;
    }

    setEliminandoTodas(true);
    try {
      const response = await eliminarReservasApi.eliminarTodasReservas("CONFIRMAR");

      if (response.data?.success) {
        const { eliminadas } = response.data;
        showToast("success", `✅ Se eliminaron ${eliminadas} reservas exitosamente`);
        setShowModalEliminarTodas(false);
        setConfirmacionTexto("");
      } else {
        showToast("error", response.data?.message || "Error al eliminar todas las reservas");
      }
    } catch (error) {
      console.error("Error al eliminar todas:", error);
      showToast("error", error.response?.data?.detail || "Error al eliminar todas las reservas");
    } finally {
      setEliminandoTodas(false);
    }
  };

  return (
    <>
      <style>
        {`
          .dh-eliminar-bg {
            background: linear-gradient(135deg, #f54242 0%, #c62828 100%);
            min-height: 100vh;
            padding: 2rem 0;
          }
          
          .dh-card-main {
            border-radius: 16px;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
            border: none;
            overflow: hidden;
          }
          
          .dh-card-header {
            background: linear-gradient(135deg, #f54242 0%, #c62828 100%);
            color: white;
            border: none;
            padding: 1.5rem;
          }
          
          .dh-header-title {
            font-weight: 600;
            color: white;
          }
          
          .dh-form-control {
            border-radius: 8px;
            border: 1px solid #e0e0e0;
            padding: 0.75rem;
            transition: all 0.3s;
          }
          
          .dh-form-control:focus {
            border-color: #f54242;
            box-shadow: 0 0 0 0.2rem rgba(245, 66, 66, 0.25);
          }
          
          .dh-btn-primary {
            background: linear-gradient(135deg, #f54242 0%, #c62828 100%);
            border: none;
            border-radius: 8px;
            padding: 0.75rem 1.5rem;
            font-weight: 500;
            transition: all 0.3s;
          }
          
          .dh-btn-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(245, 66, 66, 0.4);
          }
          
          .dh-btn-danger {
            background: #d32f2f;
            border: none;
            border-radius: 8px;
            padding: 0.75rem 1.5rem;
            font-weight: 500;
          }
          
          .dh-tabs .nav-link {
            color: #f54242;
            border: none;
            border-bottom: 2px solid transparent;
            padding: 1rem 1.5rem;
          }
          
          .dh-tabs .nav-link.active {
            color: #f54242;
            border-bottom: 2px solid #f54242;
            background: transparent;
          }
        `}
      </style>

      <div className="dh-eliminar-bg">
        <ToastContainer />
        <div className="container-fluid py-4">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-10 col-xl-8">
              <div className="card dh-card-main">
                <div className="card-header dh-card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <MdDelete className="me-3" size={28} />
                      <h3 className="dh-header-title mb-0">
                        Eliminar Reservas
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="card-body p-4">
                  {/* Proyecto */}
                  <div className="mb-4">
                    <label className="form-label fw-bold">Proyecto</label>
                    <input
                      type="text"
                      className="form-control dh-form-control"
                      value={selectedProject?.nombre || "No seleccionado"}
                      disabled
                    />
                  </div>

                  {/* Botón Descargar Reservas Actuales */}
                  <div className="mb-4 text-center">
                    <Button
                      variant="outline-primary"
                      onClick={handleDescargarReservasActuales}
                      disabled={descargando}
                      size="lg"
                    >
                      {descargando ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Descargando...
                        </>
                      ) : (
                        <>
                          <MdDownload className="me-2" />
                          Descargar Reservas Actuales
                        </>
                      )}
                    </Button>
                    <p className="text-muted small mt-2">
                      Descargue este archivo para ver todas las reservas actuales y úselo como base para eliminación masiva
                    </p>
                  </div>

                  {/* Pestañas */}
                  <Tabs
                    id="eliminar-reservas-tabs"
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="mb-4 dh-tabs"
                  >
                    <Tab eventKey="individual" title="Eliminación Individual">
                      <div className="mt-4">
                        <h5 className="mb-3">Eliminar Reserva Individual</h5>
                        <Alert variant="warning">
                          <MdWarning className="me-2" />
                          Solo se pueden eliminar reservas en estado <strong>'reservada'</strong> (sin check-in realizado)
                        </Alert>

                        <div className="mb-3">
                          <label className="form-label fw-bold">DNI del Trabajador</label>
                          <input
                            type="text"
                            className="form-control dh-form-control"
                            placeholder="Ingrese el DNI y presione Enter"
                            value={dniIndividual}
                            onChange={(e) => {
                              setDniIndividual(e.target.value);
                              setReservaEncontrada(null);
                            }}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                handleBuscarReserva();
                              }
                            }}
                            disabled={buscandoReserva}
                          />
                          {buscandoReserva && (
                            <div className="mt-2">
                              <Spinner size="sm" className="me-2" />
                              <span className="text-muted">Buscando reserva...</span>
                            </div>
                          )}
                        </div>

                        {/* Detalle de la Reserva Encontrada */}
                        {reservaEncontrada && (
                          <div className="mb-4">
                            <Alert variant="success">
                              <h6 className="mb-3">✅ Reserva Encontrada</h6>
                              <Table bordered size="sm" className="mb-0">
                                <tbody>
                                  <tr>
                                    <td className="fw-bold" style={{width: '30%'}}>DNI:</td>
                                    <td>{reservaEncontrada.dni}</td>
                                  </tr>
                                  <tr>
                                    <td className="fw-bold">Trabajador:</td>
                                    <td>{reservaEncontrada.trabajador}</td>
                                  </tr>
                                  <tr>
                                    <td className="fw-bold">Zona:</td>
                                    <td>{reservaEncontrada.zona}</td>
                                  </tr>
                                  <tr>
                                    <td className="fw-bold">Módulo:</td>
                                    <td>{reservaEncontrada.modulo}</td>
                                  </tr>
                                  <tr>
                                    <td className="fw-bold">Habitación:</td>
                                    <td>{reservaEncontrada.habitacion}</td>
                                  </tr>
                                  <tr>
                                    <td className="fw-bold">Cama:</td>
                                    <td>{reservaEncontrada.cama}</td>
                                  </tr>
                                  <tr>
                                    <td className="fw-bold">Fecha de Reserva:</td>
                                    <td>{reservaEncontrada.fecha_reserva ? new Date(reservaEncontrada.fecha_reserva).toLocaleString() : 'N/A'}</td>
                                  </tr>
                                  <tr>
                                    <td className="fw-bold">Estado:</td>
                                    <td><span className="badge bg-warning">{reservaEncontrada.estado}</span></td>
                                  </tr>
                                </tbody>
                              </Table>
                            </Alert>
                          </div>
                        )}

                        <Button
                          variant="danger"
                          onClick={handleEliminarIndividual}
                          disabled={!reservaEncontrada || eliminandoIndividual}
                          className="dh-btn-danger"
                        >
                          {eliminandoIndividual ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Eliminando...
                            </>
                          ) : (
                            <>
                              <MdDelete className="me-2" />
                              Eliminar Reserva
                            </>
                          )}
                        </Button>
                      </div>
                    </Tab>

                    <Tab eventKey="masiva" title="Eliminación Masiva">
                      <div className="mt-4">
                        <h5 className="mb-3">Eliminar Reservas Masivas</h5>
                        <Alert variant="warning">
                          <MdWarning className="me-2" />
                          Solo se eliminarán reservas en estado <strong>'reservada'</strong>
                        </Alert>

                        <p className="text-muted mb-4">
                          El archivo Excel debe contener solo una columna: <strong>DNI</strong>
                        </p>

                        <div className="mb-3">
                          <Button
                            variant="outline-primary"
                            onClick={downloadTemplateMasiva}
                            className="me-2"
                          >
                            <MdDownload className="me-2" />
                            Descargar Plantilla
                          </Button>
                        </div>

                        <div className="mb-3">
                          <label className="form-label fw-bold">Seleccionar Archivo Excel</label>
                          <input
                            type="file"
                            className="form-control dh-form-control"
                            accept=".xlsx,.xls"
                            onChange={handleFileMasivaChange}
                          />
                          {fileMasiva && (
                            <small className="text-muted">
                              Archivo seleccionado: {fileMasiva.name}
                            </small>
                          )}
                        </div>

                        <Button
                          variant="danger"
                          onClick={handleEliminarMasiva}
                          disabled={!fileMasiva || procesandoMasiva}
                          className="dh-btn-danger"
                        >
                          {procesandoMasiva ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Procesando...
                            </>
                          ) : (
                            <>
                              <MdUpload className="me-2" />
                              Eliminar Reservas
                            </>
                          )}
                        </Button>

                        {/* Resultados Masiva */}
                        {resultadosMasiva && (
                          <div className="mt-4">
                            <Alert variant={resultadosMasiva.fallidas === 0 ? "success" : "warning"}>
                              <strong>Resultado del Procesamiento:</strong>
                              <ul className="mb-0 mt-2">
                                <li><strong>Eliminadas:</strong> {resultadosMasiva.eliminadas}</li>
                                <li><strong>Fallidas:</strong> {resultadosMasiva.fallidas}</li>
                              </ul>
                            </Alert>

                            {/* Detalle Eliminadas */}
                            {resultadosMasiva.detalle_eliminadas?.length > 0 && (
                              <div className="mt-3">
                                <h6 className="text-success">✅ Reservas Eliminadas ({resultadosMasiva.detalle_eliminadas.length}):</h6>
                                <Table striped bordered hover size="sm">
                                  <thead>
                                    <tr>
                                      <th>Fila</th>
                                      <th>DNI</th>
                                      <th>Trabajador</th>
                                      <th>Zona</th>
                                      <th>Módulo</th>
                                      <th>Habitación</th>
                                      <th>Cama</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {resultadosMasiva.detalle_eliminadas.map((item, idx) => (
                                      <tr key={idx}>
                                        <td>{item.fila}</td>
                                        <td>{item.dni}</td>
                                        <td>{item.trabajador}</td>
                                        <td>{item.zona}</td>
                                        <td>{item.modulo}</td>
                                        <td>{item.habitacion}</td>
                                        <td>{item.cama}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            )}

                            {/* Detalle Fallidas */}
                            {resultadosMasiva.detalle_fallidas?.length > 0 && (
                              <div className="mt-3">
                                <h6 className="text-danger">❌ Eliminaciones Fallidas ({resultadosMasiva.detalle_fallidas.length}):</h6>
                                <Table striped bordered hover size="sm">
                                  <thead>
                                    <tr>
                                      <th>Fila</th>
                                      <th>DNI</th>
                                      <th>Error</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {resultadosMasiva.detalle_fallidas.map((item, idx) => (
                                      <tr key={idx}>
                                        <td>{item.fila}</td>
                                        <td>{item.dni}</td>
                                        <td className="text-danger">{item.error}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </Tab>

                    <Tab eventKey="todas" title="Eliminar Todas">
                      <div className="mt-4">
                        <h5 className="mb-3 text-danger">⚠️ Eliminar Todas las Reservas</h5>
                        <Alert variant="danger">
                          <MdWarning className="me-2" size={24} />
                          <strong>PRECAUCIÓN:</strong> Esta acción eliminará TODAS las reservas en estado <strong>'reservada'</strong> del proyecto actual.
                          Las reservas con check-in realizado NO serán afectadas.
                        </Alert>

                        <p className="text-muted">
                          Esta opción está diseñada para limpiar todas las reservas pendientes del proyecto de una sola vez.
                        </p>

                        <Button
                          variant="danger"
                          onClick={handleShowModalEliminarTodas}
                          size="lg"
                          className="dh-btn-danger"
                        >
                          <MdDeleteForever className="me-2" />
                          Eliminar Todas las Reservas
                        </Button>
                      </div>
                    </Tab>
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Confirmar Eliminar Todas */}
      <Modal
        show={showModalEliminarTodas}
        onHide={() => setShowModalEliminarTodas(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <MdWarning className="me-2" />
            Confirmar Eliminación Total
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <p className="mb-0">
              <strong>Esta acción es irreversible.</strong> Se eliminarán TODAS las reservas en estado 'reservada' del proyecto <strong>{selectedProject?.nombre}</strong>.
            </p>
          </Alert>
          <p>Para confirmar, escriba <strong>CONFIRMAR</strong> en el campo de abajo:</p>
          <Form.Control
            type="text"
            placeholder="Escriba CONFIRMAR"
            value={confirmacionTexto}
            onChange={(e) => setConfirmacionTexto(e.target.value.toUpperCase())}
            className="dh-form-control"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalEliminarTodas(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleEliminarTodas}
            disabled={confirmacionTexto !== "CONFIRMAR" || eliminandoTodas}
          >
            {eliminandoTodas ? (
              <>
                <Spinner size="sm" className="me-2" />
                Eliminando...
              </>
            ) : (
              <>
                <MdDeleteForever className="me-2" />
                Confirmar Eliminación
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EliminarReservas;
